import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GlobalButton from "../../utils/GlobalButton";

const COLORS = [
  {
    name: "Green",
    color: "#b7eb8f",
    bg: "#b7eb8f",
  },
  {
    name: "Blue",
    color: "#91d5ff",
    bg: "#91d5ff",
  },
  {
    name: "Orange",
    color: "#FFF3E0",
    bg: "#FFF3E0",
  },
  {
    name: "Purple",
    color: "#adc6ff",
    bg: "#adc6ff",
  },
  {
    name: "Pink",
    color: "#FCE4EC",
    bg: "#FCE4EC",
  },
  {
    name: "Teal",
    color: "#ffd591",
    bg: "#ffd591",
  },
  { name: "Red", color: "#ffa39e", bg: "#ffa39e" },
  { name: "Yellow", color: "#fff1b8", bg: "#fff1b8" },
  { name: "Cyan", color: "#b5f5ec", bg: "#b5f5ec" },
  { name: "Lavender", color: "#d3adf7", bg: "#d3adf7" },
];

const ARROW_ACTIVE = "var(--primary-color)";
const ARROW_DISABLED = "var(--new-disabled-color)";

export default function AddEditZoneDialog({
  open,
  onClose,
  editingZone,
  zones,
  setZones,
}) {
  const [zoneName, setZoneName] = useState("");

  const [selectedColor, setSelectedColor] = useState("");

  const scrollRef = useRef(null);
  const colorStripRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // -------- form state --------
  useEffect(() => {
    if (!open) return;

    if (editingZone) {
      setZoneName(editingZone.name);
      setSelectedColor(editingZone.color);
    } else {
      setZoneName("");
      setSelectedColor("");
    }
  }, [open, editingZone]);

  const updateArrows = useCallback(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    setCanScrollLeft(viewport.scrollLeft > 1);
    setCanScrollRight(maxScroll > 1 && viewport.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    if (!open) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const el = scrollRef.current;
    if (!el) return;

    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(updateArrows);
    });

    el.addEventListener("scroll", updateArrows, { passive: true });
    el.addEventListener("scrollend", updateArrows);
    window.addEventListener("resize", updateArrows);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateArrows());
      ro.observe(el);
      if (colorStripRef.current) {
        ro.observe(colorStripRef.current);
        Array.from(colorStripRef.current.children).forEach((child) =>
          ro.observe(child),
        );
      }
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      el.removeEventListener("scroll", updateArrows);
      el.removeEventListener("scrollend", updateArrows);
      window.removeEventListener("resize", updateArrows);
      ro?.disconnect();
    };
  }, [open, updateArrows]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;

    const target = el.scrollLeft + dir * 200;
    el.scrollTo({ left: target, behavior: "smooth" });

    const start = performance.now();
    const poll = () => {
      updateArrows();
      if (performance.now() - start < 600) {
        requestAnimationFrame(poll);
      } else {
        updateArrows();
      }
    };
    requestAnimationFrame(poll);
  };

  const resetForm = () => {
    setZoneName("");
    setSelectedColor("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const usedColors = useMemo(() => {
    return zones.map((item) => item.color);
  }, [zones]);

  const showAlert = (options) => {
    return Swal.fire({
      ...options,

      didOpen: (popup) => {
        const container = Swal.getContainer();

        if (container) {
          container.style.zIndex = "99999";
        }
        if (typeof options.didOpen === "function") {
          options.didOpen(popup);
        }
      },
    });
  };

  const handleSave = () => {
    const trimmedZoneName = zoneName.trim();

    if (!trimmedZoneName) {
      showAlert({
        icon: "warning",
        title: "Zone Name Required",
      });
      return;
    }

    if (!selectedColor) {
      showAlert({
        icon: "warning",
        title: "Please Select Zone Color",
      });

      return;
    }

    // Duplicate Zone Name Check
    const isDuplicate = zones.some(
      (item) =>
        item.name.toLowerCase().trim() === trimmedZoneName.toLowerCase() &&
        item.id !== editingZone?.id,
    );

    if (isDuplicate) {
      showAlert({
        icon: "error",
        title: "Duplicate Zone",
        text: "Zone name already exists.",
      });
      return;
    }

    if (editingZone) {
      const updatedZones = zones.map((item) =>
        item.id === editingZone.id
          ? {
              ...item,
              name: trimmedZoneName,
              color: selectedColor,
            }
          : item,
      );

      setZones(updatedZones);
    } else {
      const updatedZones = [
        ...zones,
        {
          id: Date.now(),
          name: trimmedZoneName,
          color: selectedColor,
        },
      ];

      setZones(updatedZones);
    }

    resetForm();

    onClose();

    showAlert({
      icon: "success",
      title: editingZone ? "Zone Updated" : "Zone Created",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: updateArrows }}
    >
      <DialogTitle>{editingZone ? "Edit Zone" : "Add Zone"}</DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Zone Name"
          value={zoneName}
          onChange={(e) => setZoneName(e.target.value)}
        />

        <Typography mt={3} mb={2} fontWeight={600}>
          Choose Zone Color
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* LEFT ARROW */}
          <IconButton
            size="small"
            onClick={() => canScrollLeft && scrollBy(-1)}
            disabled={!canScrollLeft}
            aria-label="Scroll colors left"
            sx={{
              flexShrink: 0,
              color: canScrollLeft ? ARROW_ACTIVE : ARROW_DISABLED,
              transition: "color .25s ease",
              "&.Mui-disabled": { color: ARROW_DISABLED, opacity: 1 },
              "&:hover": {
                backgroundColor: canScrollLeft
                  ? "rgba(22,119,255,0.08)"
                  : "transparent",
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
              scrollBehavior: "smooth",
              flex: "1 1 0%",
              width: 0,
              minWidth: 0,
              py: 0.5,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              ref={colorStripRef}
              sx={{
                display: "flex",
                gap: 2,
                width: "max-content",
                minWidth: "100%",
              }}
            >
              {COLORS.map((item) => {
                const disabled =
                  usedColors.includes(item.color) &&
                  editingZone?.color !== item.color;

                const isSelected = selectedColor === item.color;

                return (
                  <Box
                    key={item.color}
                    onClick={() => {
                      if (disabled) return;

                      setSelectedColor(item.color);
                    }}
                    title={item.name}
                    sx={{
                      width: 55,

                      height: 55,
                      borderRadius: "50%",
                      background: item.bg,
                      border: isSelected
                        ? "3px solid var(--all-black)"
                        : "2px solid var(--light-grey-color)",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.35 : 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: ".3s",
                      flexShrink: 0,
                      "&:hover": {
                        transform: disabled ? "none" : "scale(1.08)",
                      },
                    }}
                  >
                    {isSelected ? (
                      <CheckIcon
                        sx={{
                          color: "var(--all-black)",
                          fontSize: 26,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: item.color,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* RIGHT ARROW */}
          <IconButton
            size="small"
            onClick={() => canScrollRight && scrollBy(1)}
            disabled={!canScrollRight}
            aria-label="Scroll colors right"
            sx={{
              flexShrink: 0,
              color: canScrollRight ? ARROW_ACTIVE : ARROW_DISABLED,
              transition: "color .25s ease",
              "&.Mui-disabled": { color: ARROW_DISABLED, opacity: 1 },
              "&:hover": {
                backgroundColor: canScrollRight
                  ? "rgba(22,119,255,0.08)"
                  : "transparent",
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-3">
          <GlobalButton variant="secondary" size="medium" onClick={handleClose}>
            Cancel
          </GlobalButton>

          <GlobalButton variant="primary" size="medium" onClick={handleSave}>
            {editingZone ? "Update" : "Save"}
          </GlobalButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
